import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import Icon from '../AppIcon';
import Button from './Button';

const STORAGE_KEY = 'urbanspace_nav_tour_dismissed';
const SESSION_KEY = 'urbanspace_nav_tour_closed_session';
const TRIGGER_SELECTOR = '[data-nav-tour-trigger]';
const SHOW_DELAY_MS = 1100;
const MOBILE_QUERY = '(max-width: 1023px)';

const NAV_SECTIONS = [
  {
    label: 'Collections',
    icon: 'Grid3X3',
    description: 'Tout le catalogue, filtrable par catégorie',
  },
  {
    label: 'Remise',
    icon: 'Tag',
    description: 'Les promotions en cours',
  },
  {
    label: 'Black Friday',
    icon: 'Zap',
    description: 'L\'événement premium de la saison',
  },
];

const MobileNavTour = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [triggerRect, setTriggerRect] = useState(null);
  const prefersReducedMotion = useReducedMotion();
  const scrollLockRef = useRef(false);
  const visibleRef = useRef(false);

  const measureTrigger = useCallback(() => {
    const trigger = document.querySelector(TRIGGER_SELECTOR);
    if (!trigger) return null;

    const rect = trigger.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;

    return {
      top: rect.top,
      bottom: rect.bottom,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      centerX: rect.left + rect.width / 2,
      centerY: rect.top + rect.height / 2,
    };
  }, []);

  const lockScroll = useCallback(() => {
    if (scrollLockRef.current) return;
    scrollLockRef.current = true;
    document.body.style.overflow = 'hidden';
  }, []);

  const unlockScroll = useCallback(() => {
    if (!scrollLockRef.current) return;
    scrollLockRef.current = false;
    document.body.style.overflow = '';
  }, []);

  // Déclenchement : mobile uniquement, une fois par session, et jamais si "ne plus afficher"
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    let cancelled = false;
    let timer = null;

    const shouldShow = () => {
      if (!window.matchMedia(MOBILE_QUERY).matches) return false;
      if (localStorage.getItem(STORAGE_KEY) === 'true') return false;
      if (sessionStorage.getItem(SESSION_KEY) === 'true') return false;
      return true;
    };

    const attemptShow = () => {
      if (cancelled || visibleRef.current) return;

      const rect = measureTrigger();
      if (!rect) {
        timer = window.setTimeout(attemptShow, 400);
        return;
      }

      visibleRef.current = true;
      setTriggerRect(rect);
      setIsVisible(true);
      lockScroll();
    };

    if (shouldShow()) {
      timer = window.setTimeout(attemptShow, SHOW_DELAY_MS);
    }

    const handleResize = () => {
      if (!visibleRef.current) return;
      const rect = measureTrigger();
      if (rect) setTriggerRect(rect);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      unlockScroll();
    };
  }, [lockScroll, measureTrigger, unlockScroll]);

  // Fermeture "pour cette fois" : masqué jusqu'à la fin de la session
  const handleClose = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, 'true');
    visibleRef.current = false;
    setIsVisible(false);
    unlockScroll();
  }, [unlockScroll]);

  // Bouton check : plus jamais afficher
  const handleNeverShowAgain = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, 'true');
    sessionStorage.setItem(SESSION_KEY, 'true');
    visibleRef.current = false;
    setIsVisible(false);
    unlockScroll();
  }, [unlockScroll]);

  if (!isVisible || !triggerRect) return null;

  const spotlightRadius = Math.max(triggerRect.width, triggerRect.height) / 2 + 10;
  const arrowTop = triggerRect.bottom + 14;
  const arrowSize = prefersReducedMotion ? 40 : 44;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[70] lg:hidden" role="dialog" aria-modal="true" aria-label="Guide de navigation">
        {/* Voile flouté avec halo circulaire sur le bouton menu */}
        <motion.div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={handleClose}
          style={{
            maskImage: `radial-gradient(circle ${spotlightRadius}px at ${triggerRect.centerX}px ${triggerRect.centerY}px, transparent 0, transparent ${spotlightRadius}px, black ${spotlightRadius + 1}px)`,
            WebkitMaskImage: `radial-gradient(circle ${spotlightRadius}px at ${triggerRect.centerX}px ${triggerRect.centerY}px, transparent 0, transparent ${spotlightRadius}px, black ${spotlightRadius + 1}px)`,
          }}
        />

        {/* Anneau pulsant sur le bouton ☰ */}
        <motion.div
          className="absolute rounded-full border-2 border-[#00ffc3] pointer-events-none"
          style={{
            top: triggerRect.centerY - spotlightRadius,
            left: triggerRect.centerX - spotlightRadius,
            width: spotlightRadius * 2,
            height: spotlightRadius * 2,
            boxShadow: '0 0 24px rgba(0,255,195,0.55), inset 0 0 18px rgba(0,255,195,0.25)',
          }}
          initial={{ opacity: 0, scale: 0.7 }}
          animate={{
            opacity: 1,
            scale: [1, 1.06, 1],
          }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{
            opacity: { duration: 0.3 },
            scale: prefersReducedMotion
              ? { duration: 0.3 }
              : { duration: 1.6, repeat: Infinity, ease: 'easeInOut' },
          }}
        />

        {/* Flèche pointant vers le bouton menu */}
        <motion.div
          className="absolute pointer-events-none flex flex-col items-center"
          style={{ top: arrowTop, left: triggerRect.centerX }}
          initial={{ opacity: 0, y: -12 }}
          animate={{
            opacity: 1,
            y: 0,
            ...(prefersReducedMotion
              ? {}
              : { y: [0, -8, 0] }),
          }}
          exit={{ opacity: 0, y: -8 }}
          transition={{
            opacity: { duration: 0.3, delay: 0.15 },
            y: prefersReducedMotion
              ? { duration: 0.3, delay: 0.15 }
              : { duration: 1.4, repeat: Infinity, ease: 'easeInOut' },
          }}
        >
          <div
            className="rounded-full bg-gradient-to-br from-[#00ffc3] to-[#00aaff] flex items-center justify-center shadow-lg"
            style={{
              width: arrowSize,
              height: arrowSize,
              boxShadow: '0 0 22px rgba(0,255,195,0.6)',
            }}
          >
            <Icon name="ArrowUp" size={22} className="text-black" strokeWidth={2.5} />
          </div>
        </motion.div>

        {/* Bottom sheet */}
        <motion.div
          className="absolute bottom-0 inset-x-0 bg-surface/95 backdrop-blur-xl border-t border-[#00ffc3]/25 rounded-t-[28px] shadow-[0_-10px_50px_rgba(0,0,0,0.65)] overflow-hidden"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 26, stiffness: 260, mass: 0.7 }}
        >
          {/* Halo supérieur */}
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-24 bg-[#00ffc3]/20 rounded-full blur-3xl pointer-events-none" />

          <div className="relative px-5 pt-3 pb-6">
            {/* Poignée */}
            <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-4" />

            {/* Bouton X */}
            <button
              type="button"
              onClick={handleClose}
              aria-label="Fermer"
              className="absolute top-5 right-4 p-2 -mr-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00ffc3] transition-colors"
            >
              <Icon name="X" size={20} />
            </button>

            {/* Badge + titre */}
            <div className="flex items-start gap-3.5 pr-10">
              <div className="shrink-0 w-12 h-12 rounded-2xl bg-gradient-to-br from-[#00ffc3] to-[#00aaff] flex items-center justify-center shadow-[0_0_24px_rgba(0,255,195,0.4)]">
                <Icon name="Compass" size={24} className="text-black" strokeWidth={2.2} />
              </div>
              <div className="min-w-0">
                <h2 className="font-heading font-bold text-lg text-foreground leading-tight">
                  Vos pages sont ici
                </h2>
                <p className="text-sm text-text-secondary mt-1 leading-relaxed">
                  Touchez le bouton <span className="text-[#00ffc3] font-semibold">☰</span> en haut à droite
                  pour ouvrir le menu et naviguer dans la boutique.
                </p>
              </div>
            </div>

            {/* Les 3 sections accessibles depuis le menu */}
            <div className="mt-4 space-y-2">
              {NAV_SECTIONS.map((section, index) => (
                <motion.div
                  key={section.label}
                  className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card/50"
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.12 + index * 0.08, duration: 0.35 }}
                >
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-[#00ffc3]/10 border border-[#00ffc3]/25 flex items-center justify-center">
                    <Icon name={section.icon} size={17} className="text-[#00ffc3]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-body font-semibold text-foreground text-sm leading-tight">
                      {section.label}
                    </p>
                    <p className="text-xs text-text-secondary mt-0.5 truncate">
                      {section.description}
                    </p>
                  </div>
                  <Icon name="ChevronRight" size={16} className="shrink-0 text-muted-foreground" />
                </motion.div>
              ))}
            </div>

            {/* Actions */}
            <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2.5">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1 border-[#00ffc3]/30 text-foreground hover:bg-[#00ffc3]/10"
              >
                Plus tard
              </Button>
              <Button
                variant="default"
                onClick={handleNeverShowAgain}
                iconName="Check"
                iconPosition="left"
                className="flex-1 bg-gradient-to-r from-[#00ffc3] to-[#00aaff] text-black font-bold hover:opacity-90"
              >
                Ne plus afficher
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default MobileNavTour;
